import { ApiModel, ApiResource } from 'lib/interfaces/api';

export enum ExternalIntegrationStatus {
  Initialized = 'initialized',
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
  Unhealthy = 'unhealthy',
}

export enum DataSourceUniqueStatus {
  Processing = 'processing',
}

export type DataSourceStatus = DataSourceUniqueStatus | ExternalIntegrationStatus;

export interface ExternalIntegrationAttributes {
  name: string;
  status: ExternalIntegrationStatus;
  'created-at': string;
  'updated-at': string;
  'creator-name': string;
}

export interface ObjectPropertiesSchema {
  label: string;
  value: string;
  description?: string;
  options?: { label: string; value: string }[];
  required?: boolean;
  objectType?: string;
}

export interface Table {
  name: string;
  columns: {
    name: string;
    type: string;
    preview: string | number | boolean | null | undefined;
  }[];
}

// Data source integration attributes
export type ExternalIntegrationAttributesDataSource = ExternalIntegrationAttributes & {
  schema?: { tables: Table[] };
  status: DataSourceStatus;
};

// Enrichment integration attributes
export interface ExternalIntegrationEnrichmentChainalysisAttributes
  extends ExternalIntegrationAttributes {
  'api-key': string;
}

export interface ExternalIntegrationEnrichmentEquifaxOneviewAttributes
  extends ExternalIntegrationAttributes {
  'member-number': string;
  'security-code': string;
  'integration-environment': string;
}

export interface ExternalIntegrationEnrichmentKyckrAttributes
  extends ExternalIntegrationAttributes {
  'api-key': string;
  'integration-environment': string;
}

export interface ExternalIntegrationEnrichmentMiddeskAttributes
  extends ExternalIntegrationAttributes {
  'api-key': string;
  'integration-environment': string;
}

export interface ExternalIntegrationEnrichmentMxAttributes extends ExternalIntegrationAttributes {
  'api-key': string;
  'client-id': string;
  'integration-environment': string;
}

export interface ExternalIntegrationEnrichmentSentilinkAttributes
  extends ExternalIntegrationAttributes {
  'api-key': string;
  'account-id': string;
  'integration-environment': string;
}

export interface ExternalIntegrationEnrichmentTrmAttributes extends ExternalIntegrationAttributes {
  'api-key': string;
}

// Productivity integration attributes
export interface ExternalIntegrationHubspotOAuthAttributes extends ExternalIntegrationAttributes {
  'object-type-properties'?: Record<string, ObjectPropertiesSchema[]>;
}

export interface ExternalIntegrationOpenAiAttributes extends ExternalIntegrationAttributes {
  'api-key': string;
}

export interface ExternalIntegrationSalesforceOAuthAttributes
  extends ExternalIntegrationAttributes {
  'record-types'?: ObjectPropertiesSchema[];
  'record-type-properties'?: Record<string, ObjectPropertiesSchema[]>;
}

export interface ExternalIntegrationSlackOAuthAttributes extends ExternalIntegrationAttributes {
  'users-list'?: Record<string, string>;
  'channels-list'?: Record<string, string>;
}

export type ExternalIntegrationDataSourceCsv = ApiResource<
  ExternalIntegrationAttributesDataSource & { file: File },
  {},
  ApiModel.ExternalIntegrationDataSourceCsv
>;

type ExternalIntegrationDataSourceMongodb = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceMongodb
>;

export type ExternalIntegrationDataSourceMysql = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceMysql
>;

type ExternalIntegrationDataSourcePostgres = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourcePostgres
>;

type ExternalIntegrationDataSourceSnowflake = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceSnowflake
>;

type ExternalIntegrationDataSourceRedshift = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceRedshift
>;

type ExternalIntegrationDataSourceS3 = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceS3
>;

export type ExternalIntegrationEnrichmentEquifaxOneview = ApiResource<
  ExternalIntegrationEnrichmentEquifaxOneviewAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentEquifaxOneview
>;

export type ExternalIntegrationEnrichmentKyckr = ApiResource<
  ExternalIntegrationEnrichmentKyckrAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentKyckr
>;

export type ExternalIntegrationEnrichmentChainalysis = ApiResource<
  ExternalIntegrationEnrichmentChainalysisAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentChainalysis
>;

export type ExternalIntegrationEnrichmentMiddesk = ApiResource<
  ExternalIntegrationEnrichmentMiddeskAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentMiddesk
>;

export type ExternalIntegrationEnrichmentMx = ApiResource<
  ExternalIntegrationEnrichmentMxAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentMx
>;

export type ExternalIntegrationEnrichmentSentilink = ApiResource<
  ExternalIntegrationEnrichmentSentilinkAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentSentilink
>;

export type ExternalIntegrationEnrichmentTrm = ApiResource<
  ExternalIntegrationEnrichmentTrmAttributes,
  {},
  ApiModel.ExternalIntegrationEnrichmentTrm
>;

export type ExternalIntegrationProductivityHubspotOAuth = ApiResource<
  ExternalIntegrationHubspotOAuthAttributes,
  {},
  ApiModel.ExternalIntegrationProductivityHubspotOAuth
>;

export type ExternalIntegrationProductivityOpenAi = ApiResource<
  ExternalIntegrationOpenAiAttributes,
  {},
  ApiModel.ExternalIntegrationProductivityOpenAi
>;

export type ExternalIntegrationProductivitySalesforceOAuth = ApiResource<
  ExternalIntegrationSalesforceOAuthAttributes,
  {},
  ApiModel.ExternalIntegrationProductivitySalesforceOAuth
>;

export type ExternalIntegrationProductivitySlackOAuth = ApiResource<
  ExternalIntegrationSlackOAuthAttributes,
  {},
  ApiModel.ExternalIntegrationProductivitySlackOAuth
>;

export type ExternalIntegrationProductivityZendeskOAuth = ApiResource<
  ExternalIntegrationAttributes,
  {},
  ApiModel.ExternalIntegrationProductivityZendeskOAuth
>;

export type ExternalIntegrationDataSourceGoogleSheets = ApiResource<
  ExternalIntegrationAttributesDataSource,
  {},
  ApiModel.ExternalIntegrationDataSourceGoogleSheets
>;

export type ExternalIntegration =
  | ExternalIntegrationDataSourceCsv
  | ExternalIntegrationDataSourceGoogleSheets
  | ExternalIntegrationDataSourceMongodb
  | ExternalIntegrationDataSourceMysql
  | ExternalIntegrationDataSourcePostgres
  | ExternalIntegrationDataSourceRedshift
  | ExternalIntegrationDataSourceSnowflake
  | ExternalIntegrationEnrichmentChainalysis
  | ExternalIntegrationEnrichmentEquifaxOneview
  | ExternalIntegrationDataSourceS3
  | ExternalIntegrationEnrichmentKyckr
  | ExternalIntegrationEnrichmentMiddesk
  | ExternalIntegrationEnrichmentMx
  | ExternalIntegrationEnrichmentSentilink
  | ExternalIntegrationEnrichmentTrm
  | ExternalIntegrationProductivityHubspotOAuth
  | ExternalIntegrationProductivityOpenAi
  | ExternalIntegrationProductivitySalesforceOAuth
  | ExternalIntegrationProductivitySlackOAuth
  | ExternalIntegrationProductivityZendeskOAuth;

export type ExternalIntegrationDataSource =
  | ExternalIntegrationDataSourceCsv
  | ExternalIntegrationDataSourceGoogleSheets
  | ExternalIntegrationDataSourceMongodb
  | ExternalIntegrationDataSourceMysql
  | ExternalIntegrationDataSourcePostgres
  | ExternalIntegrationDataSourceRedshift
  | ExternalIntegrationDataSourceSnowflake
  | ExternalIntegrationDataSourceS3;

export type ExternalIntegrationDataSourceModels =
  | ApiModel.ExternalIntegrationDataSourceCsv
  | ApiModel.ExternalIntegrationDataSourceGoogleSheets
  | ApiModel.ExternalIntegrationDataSourceMongodb
  | ApiModel.ExternalIntegrationDataSourceMysql
  | ApiModel.ExternalIntegrationDataSourcePostgres
  | ApiModel.ExternalIntegrationDataSourceRedshift
  | ApiModel.ExternalIntegrationDataSourceSnowflake
  | ApiModel.ExternalIntegrationDataSourceS3;

export const ExternalIntegrationDataSourceTypes = [
  ApiModel.ExternalIntegrationDataSourceCsv,
  ApiModel.ExternalIntegrationDataSourceGoogleSheets,
  ApiModel.ExternalIntegrationDataSourceMongodb,
  ApiModel.ExternalIntegrationDataSourceMysql,
  ApiModel.ExternalIntegrationDataSourcePostgres,
  ApiModel.ExternalIntegrationDataSourceRedshift,
  ApiModel.ExternalIntegrationDataSourceSnowflake,
  ApiModel.ExternalIntegrationDataSourceS3,
];

export const EXTERNAL_INTEGRATION_DISPLAY_NAME: Record<ExternalIntegration['type'], string> = {
  [ApiModel.ExternalIntegrationDataSourceCsv]: 'CSV',
  [ApiModel.ExternalIntegrationDataSourceGoogleSheets]: 'Google Sheets',
  [ApiModel.ExternalIntegrationDataSourceMongodb]: 'MongoDB',
  [ApiModel.ExternalIntegrationDataSourceMysql]: 'MySQL',
  [ApiModel.ExternalIntegrationDataSourcePostgres]: 'Postgres',
  [ApiModel.ExternalIntegrationDataSourceRedshift]: 'Redshift',
  [ApiModel.ExternalIntegrationDataSourceSnowflake]: 'Snowflake',
  [ApiModel.ExternalIntegrationDataSourceS3]: 'S3',
  [ApiModel.ExternalIntegrationEnrichmentChainalysis]: 'Chainalysis',
  [ApiModel.ExternalIntegrationEnrichmentEquifaxOneview]: 'Equifax',
  [ApiModel.ExternalIntegrationEnrichmentKyckr]: 'Kyckr',
  [ApiModel.ExternalIntegrationEnrichmentMiddesk]: 'Middesk',
  [ApiModel.ExternalIntegrationEnrichmentMx]: 'MX',
  [ApiModel.ExternalIntegrationEnrichmentSentilink]: 'SentiLink',
  [ApiModel.ExternalIntegrationEnrichmentTrm]: 'TRM',
  [ApiModel.ExternalIntegrationProductivityHubspotOAuth]: 'HubSpot',
  [ApiModel.ExternalIntegrationProductivityOpenAi]: 'OpenAI',
  [ApiModel.ExternalIntegrationProductivitySalesforceOAuth]: 'Salesforce',
  [ApiModel.ExternalIntegrationProductivitySlackOAuth]: 'Slack',
  [ApiModel.ExternalIntegrationProductivityZendeskOAuth]: 'Zendesk',
  // NB(sal): This is a hack to enable Mastercard Integration training. we could delete this when we remove enable-integration-tab flipper
  ['mastercard-identity' as ExternalIntegration['type']]: 'Mastercard Identity',
};

export interface S3Region {
  value: string;
  label: string;
}

export interface S3Config {
  persona_aws_account_id: string;
  available_regions: S3Region[];
}

export interface AwsCrossAccountRoleCredentials {
  type: 'cross_account';
  arn: string;
  externalId: string;
}

export interface S3Object {
  key: string;
  size: number;
}

export interface GoogleSheetsFilesResponse {
  files: string[][];
  token: string;
}
